import React, { useState, useEffect } from 'react';
import LoginPage from './components/Auth/LoginPage';
import PaymentMethodReport from './components/Reports/PaymentMethodReport';
import './index.css';

// Simple Dashboard Component
const Dashboard = ({ user, selectedTenant }) => {
  const [overview, setOverview] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const API_BASE = 'https://teranetsystemspos.venvirtual.net/backend-php/reports_api4.php';

  useEffect(() => {
    if (currentView === 'dashboard') {
      fetch(`${API_BASE}?action=dashboard&tenant_id=${selectedTenant?.id}`)
        .then(res => res.json())
        .then(data => {
          setOverview(data.data || {});
          setLoading(false);
        })
        .catch(err => {
          console.error('Dashboard error:', err);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, [selectedTenant, currentView]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl">Loading...</div>
      </div>
    );
  }

  // Render different views
  const renderCurrentView = () => {
    switch (currentView) {
      case 'dashboard':
        return (
          <div className="p-6">
            <h1 className="text-3xl font-bold mb-6">Sales Dashboard</h1>
            
            {/* Today's Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-sm text-gray-600 mb-2">Today's Sales</h3>
                <p className="text-2xl font-bold text-green-600">
                  ${(overview.today_sales || 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">{overview.today_transactions || 0} transactions</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-sm text-gray-600 mb-2">Today's Returns</h3>
                <p className="text-2xl font-bold text-red-600">
                  ${(overview.today_returns || 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">Returns processed</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-sm text-gray-600 mb-2">This Month</h3>
                <p className="text-2xl font-bold text-blue-600">
                  ${(overview.month_sales || 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-500">{overview.active_days || 0} active days</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-sm text-gray-600 mb-2">Low Stock Alert</h3>
                <p className="text-2xl font-bold text-orange-600">
                  {overview.low_stock_count || 0}
                </p>
                <p className="text-sm text-gray-500">Items need reorder</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📊 Sales Report</h3>
                <button 
                  onClick={() => setCurrentView('payment-method')}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  View Payment Report
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📦 Products</h3>
                <button className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">
                  Manage Products
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📈 Analytics</h3>
                <button className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700">
                  View Analytics
                </button>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📤 Import Data</h3>
                <button className="w-full bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">
                  Import Sales
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white p-6 rounded-lg shadow">
              <h2 className="text-xl font-semibold mb-4">Recent Activity</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium">Sales data imported</div>
                    <div className="text-sm text-gray-600">125 records processed</div>
                  </div>
                  <div className="text-sm text-gray-500">2 hours ago</div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium">Low stock alert</div>
                    <div className="text-sm text-gray-600">3 items need reorder</div>
                  </div>
                  <div className="text-sm text-gray-500">5 hours ago</div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div>
                    <div className="font-medium">Daily report generated</div>
                    <div className="text-sm text-gray-600">Yesterday's summary</div>
                  </div>
                  <div className="text-sm text-gray-500">1 day ago</div>
                </div>
              </div>
            </div>
          </div>
        );
        
      case 'payment-method':
        return <PaymentMethodReport selectedTenant={selectedTenant} />;
        
      default:
        return <div>View not found</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <img 
                src="/teranet-logo.png" 
                alt="Teranet Systems" 
                className="h-8 mr-4"
                onError={(e) => {
                  e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='40' viewBox='0 0 120 40'%3E%3Crect width='120' height='40' fill='%234F46E5'/%3E%3Ctext x='60' y='25' font-family='Arial, sans-serif' font-size='14' font-weight='bold' fill='white' text-anchor='middle'%3ETERANET%3C/text%3E%3C/svg%3E";
                }}
              />
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Teranet Reports</h1>
                <p className="text-sm text-gray-600">{selectedTenant.name}</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Navigation */}
              <nav className="flex space-x-4">
                <button
                  onClick={() => setCurrentView('dashboard')}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    currentView === 'dashboard' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Dashboard
                </button>
                <button
                  onClick={() => setCurrentView('payment-method')}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    currentView === 'payment-method' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Payment Report
                </button>
              </nav>
              
              <div className="text-right">
                <div className="text-sm font-medium text-gray-900">{user.first_name} {user.last_name}</div>
                <div className="text-sm text-gray-600">{user.role}</div>
              </div>
              <button
                onClick={() => {
                  setUser(null);
                  setSelectedTenant(null);
                  setShowTenantSelection(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto">
        {renderCurrentView()}
      </main>
    </div>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [availableTenants, setAvailableTenants] = useState([]);

  const API_BASE_URL = 'https://teranetsystemspos.venvirtual.net/backend-php/public';

  useEffect(() => {
    // Check API connection
    fetch(`${API_BASE_URL}/`)
      .then(response => response.json())
      .then(data => {
        console.log('API Status:', data);
      })
      .catch(error => {
        console.error('API connection failed:', error);
        setError('Unable to connect to server. Please check your connection.');
      });
  }, []);

  const handleLogin = async (loginData) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      if (data.success) {
        setUser(data.user);
        setAvailableTenants(data.availableTenants || []);
        
        if (data.availableTenants && data.availableTenants.length > 1) {
          setShowTenantSelection(true);
        } else if (data.availableTenants && data.availableTenants.length === 1) {
          setSelectedTenant(data.availableTenants[0]);
        }
      } else {
        setError(data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = (tenant) => {
    setSelectedTenant(tenant);
    setShowTenantSelection(false);
  };

  // Show tenant selection if needed
  if (showTenantSelection) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Location</h2>
          <div className="space-y-3">
            {availableTenants.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => handleTenantSelect(tenant)}
                className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
              >
                <div className="font-semibold text-gray-900">{tenant.name}</div>
                <div className="text-sm text-gray-600">Role: {tenant.role}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Show dashboard if logged in
  if (user && selectedTenant) {
    return <Dashboard user={user} selectedTenant={selectedTenant} />;
  }

  // Show login page
  return <LoginPage onLogin={handleLogin} loading={loading} error={error} />;
}

export default App;
